/**
 * "Building a custom experience" — Widget section.
 *
 * Replaces the former "headless SDK" page, which described a standalone headless
 * client that Syncanix does NOT ship (the widget package exports only
 * `./preview`; there is no headless entry point). This page honestly maps the
 * real deep-customization surface that DOES ship — inline mounting, registered
 * components/actions/data, theming, and the runtime API — and is clear that a
 * fully headless "engine-only" package is not offered today.
 */
import type { DocPageContent } from '../types';

export const WIDGET_CUSTOM_UI_PAGE: DocPageContent = {
  slug: 'widget/custom-ui',
  titleKey: 'docs.widget.customUi.title',
  descriptionKey: 'docs.widget.customUi.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.customUi.lead' },

    { kind: 'heading', level: 2, id: 'toolbox', textKey: 'docs.widget.customUi.toolbox.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.customUi.toolbox.body' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.customUi.tool.inline.title',
          textKey: 'docs.widget.customUi.tool.inline.text',
          to: { docSlug: 'widget/embedding' },
        },
        {
          titleKey: 'docs.widget.customUi.tool.extend.title',
          textKey: 'docs.widget.customUi.tool.extend.text',
          to: { docSlug: 'widget/extending' },
        },
        {
          titleKey: 'docs.widget.customUi.tool.theme.title',
          textKey: 'docs.widget.customUi.tool.theme.text',
          to: { docSlug: 'widget/theming' },
        },
        {
          titleKey: 'docs.widget.customUi.tool.api.title',
          textKey: 'docs.widget.customUi.tool.api.text',
          to: { docSlug: 'widget/runtime-api' },
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'how-far', textKey: 'docs.widget.customUi.howFar.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.customUi.howFar.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.customUi.howFar.item.place',
        'docs.widget.customUi.howFar.item.brand',
        'docs.widget.customUi.howFar.item.components',
        'docs.widget.customUi.howFar.item.actions',
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'fully-headless',
      textKey: 'docs.widget.customUi.headless.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.customUi.headless.body' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.widget.customUi.headless.note.title',
      textKey: 'docs.widget.customUi.headless.note.body',
      links: {
        contact: {
          target: { path: '/contact' },
          labelKey: 'docs.widget.customUi.headless.note.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.customUi.next.embedding.title',
          textKey: 'docs.widget.customUi.next.embedding.text',
          to: { docSlug: 'widget/embedding' },
        },
        {
          titleKey: 'docs.widget.customUi.next.extending.title',
          textKey: 'docs.widget.customUi.next.extending.text',
          to: { docSlug: 'widget/extending' },
        },
      ],
    },
  ],
};
